<template>
  <div class="records-table">
    <div class="toolbar">
      <slot name="RecordsMenuButton"></slot>
      <InputGroup style="width: initial;">
        <slot name="date-from"></slot>
        <slot name="date-to"></slot>
        <Button v-if="showingClearTimeFilter" severity="secondary" style="color: #444" @click="clearTimeFilter()">
          <i class="pi pi-times"></i>
        </Button>
        <Button severity="secondary" style="color: #444" @click="refreshMenu()">
          <i class="pi pi-refresh"></i>
        </Button>
      </InputGroup>
      <slot name="selectedTemperatureUnit"></slot>
      <Dropdown v-model="order" :options="orderOptions" optionLabel="label" optionValue="value"></Dropdown>
      <Dropdown v-model="granularity" :options="granularityOptions" optionLabel="caption" optionValue="value" optionDisabled="disabled"></Dropdown>
    </div>

    <div class="table-scroll" @scroll="onScroll($event)">
      <table class="records-table--table">
        <thead>
          <tr v-if="selectableBins.length > 2">
            <th :rowspan="timestampSpan">
              Timestamp
            </th>
            <th v-for="columnGroup in columnGroups" :colspan="columnGroupSpan(columnGroup)">
              {{ columnGroup.columnName }}
            </th>
          </tr>
          <tr>
            <th v-if="selectableBins.length == 2" :rowspan="timestampSpan">
              Timestamp
            </th>
            <template v-for="columnGroup in columnGroups">
              <th v-if="showingEvents">
                Events
              </th>
              <th v-for="control in relevantControls(columnGroup)" :colspan="controlSpan(control)">
                {{ control.name }}
                <span style="font-style: italic; font-size: small;" 
                  v-html="controlUnit(control)"
                ></span>
              </th>
            </template>
          </tr>
          <tr v-if="showingSensors">
            <template v-for="columnGroup in columnGroups">
              <th v-if="showingEvents"></th>
              <template v-for="control in relevantControls(columnGroup)">
                <th v-if="control.selected" class="control-hdr">
                  {{ selectedSensors(control).length ? 'AVG' : '' }}
                </th>
                <th v-for="sensor in selectedSensors(control)">
                  {{ sensor.name }}
                </th>
              </template>
            </template>
          </tr>
        </thead>
      </table>
    </div>

  </div>

</template>

<script>
import store from "./store.js";
import { convertValue, convertUnit, unitRound, unitHtml } from "./units.js";
import { zoneDate } from "./timezones.js";
import { formatLongTimestamp } from './timestamps.js';
import { last } from "./utility.js";
import RecordsMenuButton from "./RecordsMenuButton.vue";

const pageSize = 150;

export default {
  components: { RecordsMenuButton },
  props: [
    "tags",
    "bins",
    "relevantBins",
    "controls",
    "events",
    "timeSegments",
    "selectableBins",
    "showingMenu",
    "recordsMenuRefresh",
    "selectedTemperatureUnit",
    "normalized_timeFrom",
    "normalized_timeTo",
    "dateFrom",
    "dateTo"
  ],
  data() {
    return {
      store,
      order: "desc",
      granularity: localStorage.getItem("records-granularity") || "granularity-900s",
      pageEnd: null,
      pendingQuery: null,
      previousScrollTop: 0
    }
  },
  computed: {
    showingClearTimeFilter() {
      return (this.normalized_timeFrom || this.normalized_timeTo);
    },
    orderOptions() {
      return [
        { label: "Newest first", value: "desc" },
        { label: "Oldest first", value: "asc" }
      ];
    },
    granularityOptions() {
      return [
        { value: 'granularity-1s', caption: 'Granularity: 1 sec' },
        { value: 'granularity-60s', caption: 'Granularity: 1 min' },
        { value: 'granularity-300s', caption: 'Granularity: 5 min' },
        { value: 'granularity-900s', caption: 'Granularity: 15 min' },
        { value: 'granularity-event', caption: 'Granularity: Selected events', disabled: !this.showingEvents }
      ];
    },
    granularityLabel() {
      let selectedOption = this.granularityOptions.find(opt => opt.value == this.granularity);
      return selectedOption.caption;
    },
    selectedControls() {
      return this.controls;
    },
    selectedBinEvents() {
      let selectedBinEvents = [];
      this.events.forEach(event => {
        this.relevantBins.forEach(bin => selectedBinEvents.push([bin.id, event.eventCode]));
      });
      return selectedBinEvents;
    },
    showingEvents() {
      return this.events.some(event => event.selected);
    },
    showingSensors() {
      let aggrs = this.relevantBins.flatMap(bin => bin.aggrs);
      let sensors = aggrs.flatMap(aggr => aggr.sensors);
      return sensors.some(sensor => sensor.selected);
    },
    timestampSpan() {
      return this.showingSensors ? 3 : 2;
    },
    columnGroups() {
      if (this.selectableBins.length > 2) {
        return this.relevantBins.map(bin => {
          return {
            columnName: bin.name,
            bins: [ bin ],
            aggrs: bin.aggrs,
            outputs: bin.outputs
          };
        });
      }
      else {
        let system = this.selectableBins[0];
        let bin = this.selectableBins[1];
        return [{
          columnName: store.sessions[0].system.boardName.value,
          bins: [ system, bin ],
          aggrs: [ ... system.aggrs, ... bin.aggrs ],
          outputs: [ ... system.outputs, ... bin.outputs ]
        }];
      }
    },
    numColumns() {
      let count = 0;
      this.columnGroups.forEach(columnGroup => count += this.columnGroupSpan(columnGroup))
      return count;
    },
    queryArgs() {
      return [ this.relevantBins, this.selectedBinEvents, this.normalized_timeFrom, this.normalized_timeTo, this.granularity, this.order ];
    }
  },
  watch: {
    showingEvents() {
      let shouldResetGranularity = !this.showingEvents && this.granularity == 'granularity-event';
      if (shouldResetGranularity)
        this.granularity = "granularity-900s";
    },
    granularity() {
      localStorage.setItem("records-granularity", this.granularity);
    },
    selectedTemperatureUnit() {
      this.refresh();
    },
    queryArgs: {
      deep: true,
      handler() {
        this.refresh();
      }
    },
    columnGroups() {
      this.refresh();
    }
  },
  mounted() {
    this.tableElem = this.$el.querySelector("table");
    this.runQuery();
  },
  methods: {
    unitHtml,
    clearTimeFilter() {
      this.$emit("update:normalized_timeFrom", null);
      this.$emit("update:normalized_timeTo", null);
    },
    refreshMenu() {
      this.pageEnd = null;
      this.clearTable();
      this.recordsMenuRefresh(); // Triggers call to refresh().
    },
    refresh() {
      this.pageEnd = null;
      this.clearTable();
      this.runQuery();
    },
    columnGroupSpan(columnGroup) {
      let span = this.showingEvents ? 1 : 0;
      let controls = [ ... columnGroup.aggrs, ... columnGroup.outputs ];
      controls.forEach(control => span += this.controlSpan(control));
      return span;
    },
    relevantControls(columnGroup) {
      return [
        ... columnGroup.aggrs.filter(aggr => aggr.selected || aggr.sensors.some(sensor => sensor.selected)),
        ... columnGroup.outputs.filter(output => output.selected)
      ]
    },
    controlSpan(control) {
      return this.selectedSensors(control).length + (control.selected ? 1 : 0);
    },
    controlUnit(control) {
      return "";
    },
    selectedSensors(control) {
      if (control.sensors)
        return control.sensors.filter(sensor => sensor.selected);
      else
        return [];
    },
    runQuery() {
      if (this.pendingQuery)
        this.pendingQuery.cancel();
      
      let pageStart = null;
      if (this.order == "desc")
        pageStart = this.pageEnd ? this.pageEnd - 1 : 0x7fffffff;
      else
        pageStart = this.pageEnd ? this.pageEnd + 1 : 0;

      let session = store.sessions[0];
      this.pendingQuery = session.queueRequest("/cmd/queryTable", {
        granularity: this.granularity,
        bins: this.bins.filter(bin => bin.selected).map(bin => bin.id),
        controls: this.selectedControls.map(control => control.id),
        events: this.events.map(event => event.eventCode),
        timeFrom: this.normalized_timeFrom,
        timeTo: this.normalized_timeTo,
        pageStart: pageStart,
        order: this.order,
        pageLength: pageSize
      }, this.handleResults);
    },
    handleResults(results) {
      if (results.timestamps.length == 0)
       return;

      let numRowsInResponse = results.timestamps.length;
      let numRowsToUse = Math.min(pageSize, numRowsInResponse);
      results.timestamps.length = numRowsToUse;
      this.pageEnd = last(results.timestamps)[0];
      
      let h = document.createElement.bind(document);
      let tbody = document.createElement("tbody");

      let eventCellsMap = {};
      let controlCellsMap = {};
      let i = 0;
      while (i < results.timestamps.length) {
        // TODO: Resume on last <tr> when paginating.
        let tr = h("tr"); tbody.appendChild(tr);
        let th = h("th"); tr.appendChild(th);

        let _timestamp = results.timestamps[i][1];
        let zoned = zoneDate(_timestamp * 1000, store.sessions[0].system.timezone.value);
        th.innerText = formatLongTimestamp(zoned);

        // Add the event and analog cells for each bin.
        this.columnGroups.forEach((columnGroup, columnGroupIndex) => {
          if (this.showingEvents) {
            let td = h("td"); tr.appendChild(td);
            td.style.width = (100 / this.numColumns) + "%";
            td.className = "event";
            eventCellsMap[columnGroupIndex] = td;
          }
          this.relevantControls(columnGroup).forEach(control => {
            let _controls = [ control, ... (control.sensors) ? control.sensors : [] ];
            let selectedControls = _controls.filter(control => control.selected);
            selectedControls.forEach(selectedControl => {
              let td = h("td"); tr.appendChild(td);
              td.style.width = (100 / this.numColumns) + "%";
              controlCellsMap[selectedControl.id] = td;
            });
          });
        });

        // Fill out the row's cells.
        let prevTimestamp = null;
        let prevControlRecords = null;
        let lookup = tagId => this.tags.find(tag => tag.tagId == tagId);
        for (; i < results.timestamps.length; i++) {
          // Break if this record's timestamp is different - a new row is needed for each distinct timestamp.
          let [recordNumber, timestamp] = results.timestamps[i];
          if (timestamp != prevTimestamp && prevTimestamp != null) break;
          prevTimestamp = timestamp;
          // Break if this is the second record with analog values - a new row is needed for each set of analog values.
          let controlRecords = results.controlRecords[recordNumber];
          if (controlRecords && prevControlRecords) break;
          prevControlRecords = controlRecords;
          // Append to the appropriate event cell if this is an event record.
          let eventRecord = results.eventRecords[recordNumber];
          if (eventRecord) {
            let tagId = eventRecord[0];
            let eventCode = eventRecord[1];
            let eventData = this.events.find(event => event.eventCode == eventCode);
            let tags = (() => {
              let result = [];
              let tag = lookup(tagId);
              while (tag != null) {
                result.unshift(tag);
                tag = lookup(tag.parentId);
              }
              return result;
            })();
            let bin = tags.shift();
            let columnGroupIndex = this.columnGroups.findIndex(columnGroup => columnGroup.bins.find(_bin => _bin.id == bin.tagId));
            let td = eventCellsMap[columnGroupIndex];
            td.append(tags.map(tag => tag.tagName).join(" ") + " ");
            td.append(eventData.eventShortName || eventData.eventName);
            td.append(h("br"));
          }
          Object.keys(controlRecords || {}).forEach(controlId => {
            let control = this.controls.find(control => control.id == controlId); //lookup(controlId);
            let controlValue = controlRecords[controlId];
            let td = controlCellsMap[controlId];
            if (controlValue == null) {
              td.innerText = "Comm error";
            }
            else {
              let convertedValue = convertValue(controlValue, control.unit, this.selectedTemperatureUnit);
              let convertedUnit = convertUnit(control.unit, this.selectedTemperatureUnit);
              let precision = (1 / Math.pow(10, control.decimals ?? 1));
              td.innerHTML = `${unitRound(convertedValue, convertedUnit, precision)} ${unitHtml(convertedUnit)}`;
            }
          });
        }
      }
      this.tableElem.appendChild(tbody);
    },
    onScroll(event) {
      let elem = event.target;
      let scrollDiff = elem.scrollHeight - (elem.scrollTop + elem.clientHeight);
      let epsilon = 1;  // Page zoom can cause imprecision.
      let isBottomScroll = scrollDiff <= epsilon;
      let isVerticalScroll = this.previousScrollTop !== elem.scrollTop;
      let isScrollToBottom = isVerticalScroll && isBottomScroll;
      this.previousScrollTop = elem.scrollTop;
      if (isScrollToBottom)
        this.runQuery();
    },
    clearTable() {
      // taking care not to remove the "thead" because it is built by vue
      let tbodies = [ ... this.tableElem.querySelectorAll("tbody") ]
      tbodies.forEach(tbody => tbody.parentNode.removeChild(tbody));      
    }
  }
}
</script>

<style>
.records-table {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.records-table .toolbar {
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  gap: 10px;
}
.records-table .toolbar .p-dropdown {
  display: flex;
  align-items: center;
}
.records-table .table-scroll {
  overflow: auto;
  flex-grow: 1;
  mask-image: linear-gradient(to bottom, black calc(100% - 28px), transparent 100%);
  padding-bottom: 28px;
}
/* styles can't be scoped in this component because handleResults() has no idea what the generated class names would be */
.records-table--table {
  width: 100%;
  font-size: 13px;
  border-collapse: separate;
  border-spacing: 0;
  border-right: 1px solid #aaa;
}
.records-table--table thead {
  position: sticky;
  top: 0;
  z-index: 10;
}
.records-table--table th,
.records-table--table td {
  border-bottom: 1px solid #aaa;
  border-left: 1px solid #aaa;
}
.records-table--table thead tr:first-child th:first-child,
.records-table--table tbody th:first-child
{
  position: sticky;
  left: 0;
  border-right: 1px solid #aaa;
}
.records-table--table thead tr:first-child th {
  border-top: 1px solid #aaa;
}
/* .records-table--table thead tr:first-child th:nth-child(2),
.records-table--table thead tr:nth-child(2) th:first-child,
.records-table--table thead tr:nth-child(3) th:first-child,
.records-table--table td:first-of-type {
  border-left: 0;
} */
.records-table--table th {
  background: whitesmoke;
  padding-left: 10px;
  padding-right: 10px;
  font-weight: normal;
}
.records-table--table th,
.records-table--table td {
  text-align: center;
  white-space: nowrap;
}
.records-table--table td {
  padding-left: 5px;
  padding-right: 5px;
}
.records-table--table thead,
.records-table--table .event 
{
  /* font-family: Verdana, Geneva, Tahoma, sans-serif; */
}
.records-table--table tbody {
  font-family: Consolas;
}
</style>